import type { AxiosInstance } from "axios";

import type { CreateFolderRequest } from "@/types/requests";
import type {
  CreateFolderResponse,
  GetFoldersResponse,
} from "@/types/responses";

import { TRACKER_PREFIX } from "./survey";

/* Tracker studies are filed in "Midterms 2026 › Wave N", apart from the
   user's own study folders. */

const sameName = (a: string, b: string): boolean =>
  a.trim().toLowerCase() === b.trim().toLowerCase();

/**
 * Lists the user's study folders once, then resolves a wave number to its
 * folder id, reusing folders with the right name (any case) and creating
 * the missing ones. `client` is based on `/folders`.
 */
export const waveFolders = async (
  client: AxiosInstance
): Promise<(wave: number) => Promise<string>> => {
  const { data } = await client.get<GetFoldersResponse>("", {
    params: { type: "studies" },
  });
  // The list also returns archived subfolders, which can't take new studies.
  const folders = data.folders.filter((f) => f.archived_at === null);

  const find = (name: string, parent: string | null) =>
    folders.find((f) => f.parent === parent && sameName(f.name, name));
  const create = async (name: string, parent: string | null) => {
    const { data: created } = await client.post<CreateFolderResponse>("", {
      name,
      parent_id: parent,
      type: "studies",
    } satisfies CreateFolderRequest);
    folders.push(created.folder);
    return created.folder;
  };

  return async (wave) => {
    const root =
      find(TRACKER_PREFIX, null) ?? (await create(TRACKER_PREFIX, null));
    const name = `Wave ${wave}`;
    return (find(name, root.id) ?? (await create(name, root.id))).id;
  };
};
